import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const LS_KEY = 'nnl-ambience';

// Soft boutique ambience synthesized with the Web Audio API (no audio file needed).
const buildAudio = () => {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.destination);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 620;
  filter.connect(master);

  const nodes = [];
  [[110, 0.30], [164.81, 0.22], [220, 0.16], [277.18, 0.08]].forEach(([freq, vol], i) => {
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = vol;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05 + i * 0.028;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 3.2;
    lfo.connect(lfoGain).connect(osc.detune);
    osc.connect(g).connect(filter);
    osc.start(); lfo.start();
    nodes.push(osc, lfo);
  });

  // gentle "air" noise
  const len = ctx.sampleRate * 2;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
  const noise = ctx.createBufferSource();
  noise.buffer = buf; noise.loop = true;
  const nFilter = ctx.createBiquadFilter();
  nFilter.type = 'lowpass'; nFilter.frequency.value = 420;
  const nGain = ctx.createGain(); nGain.gain.value = 0.02;
  noise.connect(nFilter).connect(nGain).connect(master);
  noise.start();
  nodes.push(noise);

  // slow breathing on the master level
  const breath = ctx.createOscillator();
  breath.frequency.value = 0.07;
  const breathGain = ctx.createGain();
  breathGain.gain.value = 0.012;
  breath.connect(breathGain).connect(master.gain);
  breath.start();
  nodes.push(breath);

  return { ctx, master, nodes };
};

const AmbientSound = () => {
  const [on, setOn] = useState(() => localStorage.getItem(LS_KEY) === 'on');
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const start = () => {
    if (audioRef.current) return;
    const a = buildAudio();
    audioRef.current = a;
    a.ctx.resume();
    a.master.gain.setTargetAtTime(0.055, a.ctx.currentTime, 1.2);
    setPlaying(true);
  };

  const stop = () => {
    const a = audioRef.current;
    if (!a) return;
    a.master.gain.setTargetAtTime(0, a.ctx.currentTime, 0.4);
    setTimeout(() => { try { a.nodes.forEach((n) => n.stop?.()); a.ctx.close(); } catch (e) { if (process.env.NODE_ENV !== 'production') console.debug('ambient stop failed', e); } }, 1400);
    audioRef.current = null;
    setPlaying(false);
  };

  // if saved "on", wait for the first user gesture (autoplay policy)
  useEffect(() => {
    if (!on || audioRef.current) return undefined;
    const resume = () => start();
    document.addEventListener('pointerdown', resume, { once: true });
    return () => document.removeEventListener('pointerdown', resume);
  }, [on]);

  useEffect(() => () => stop(), []);

  const toggle = () => {
    if (on) { stop(); setOn(false); localStorage.setItem(LS_KEY, 'off'); }
    else { start(); setOn(true); localStorage.setItem(LS_KEY, 'on'); }
  };

  return (
    <button
      onClick={toggle}
      type="button"
      aria-label={on ? 'Turn boutique ambience off' : 'Turn boutique ambience on'}
      title={on ? 'Ambience on' : 'Boutique ambience'}
      data-testid="ambience-toggle"
      className={`fixed bottom-5 right-5 z-[60] w-11 h-11 rounded-full flex items-center justify-center border backdrop-blur transition-colors ${on ? 'bg-[#0b0b0b]/90 border-[#e7d3a1] text-[#e7d3a1]' : 'bg-[#0b0b0b]/70 border-[#c6a15b]/40 text-[#9c9587] hover:text-[#e7d3a1] hover:border-[#c6a15b]'}`}
    >
      {on ? <Volume2 size={17} /> : <VolumeX size={17} />}
      {on && playing && (
        <span className="absolute inset-0 rounded-full border border-[#e7d3a1]/50 animate-ping" aria-hidden="true" />
      )}
    </button>
  );
};

export default AmbientSound;
